<template>
  <div ref="rootEl" class="w-full relative h-[20px]">
    <svg class="absolute inset-0 size-full overflow-visible" fill="none">
      <path :d="path" stroke-width="1" :class="tint.stroke" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    markerPct: number
    legX?: number
    level?: ScoreLevel | null
  }>(),
  { legX: 0, level: null }
)

const BAR_Y = 13
const BOTTOM_Y = 20
const RADIUS = 6

const tint = computed(() => {
  if (props.level === ScoreLevel.High) {
    return { stroke: 'stroke-red-300' }
  }
  if (props.level === ScoreLevel.Medium) {
    return { stroke: 'stroke-yellow-400' }
  }
  if (props.level === ScoreLevel.Low) {
    return { stroke: 'stroke-green-400' }
  }
  return { stroke: 'stroke-gray-300' }
})

const rootEl = ref<HTMLElement>()
const width = ref(0)
let resizeObserver: ResizeObserver | null = null

const markerX = computed(() => {
  const w = width.value
  if (!w) return 0
  const raw = (props.markerPct / 100) * w
  return Math.min(Math.max(raw, props.legX + RADIUS), w - RADIUS)
})

const path = computed(() => {
  const w = width.value
  if (!w) return ''
  const mx = markerX.value
  const xl = props.legX
  const xr = w
  const r = RADIUS
  const yBar = BAR_Y
  const yBot = BOTTOM_Y
  return [
    `M ${xl} ${yBot}`,
    `L ${xl} ${yBar + r}`,
    `Q ${xl} ${yBar} ${xl + r} ${yBar}`,
    `L ${mx - r} ${yBar}`,
    `Q ${mx} ${yBar} ${mx} ${yBar - r}`,
    `L ${mx} 0`,
    `M ${xr} ${yBot}`,
    `L ${xr} ${yBar + r}`,
    `Q ${xr} ${yBar} ${xr - r} ${yBar}`,
    `L ${mx + r} ${yBar}`,
    `Q ${mx} ${yBar} ${mx} ${yBar - r}`,
    `L ${mx} 0`,
  ].join(' ')
})

onMounted(() => {
  if (!rootEl.value) return
  resizeObserver = new ResizeObserver(() => {
    width.value = rootEl.value?.clientWidth ?? 0
  })
  resizeObserver.observe(rootEl.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>
